import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const EditAttendanceModal = ({ isOpen, onClose, record, onSave }) => {
  const [formData, setFormData] = useState({
    employeeName: '',
    employeeId: '',
    date: '',
    checkIn: '',
    checkOut: '',
    status: 'present',
    notes: '',
    department: ''
  });

  const statusOptions = [
    { value: 'present', label: 'Present' },
    { value: 'absent', label: 'Absent' },
    { value: 'halfday', label: 'Half Day' },
    { value: 'late', label: 'Late Arrival' },
    { value: 'early_departure', label: 'Early Departure' },
    { value: 'on_leave', label: 'On Leave' }
  ];

  useEffect(() => {
    if (record) {
      setFormData({
        employeeName: record?.employeeName || '',
        employeeId: record?.employeeId || '',
        date: record?.date || '',
        checkIn: record?.checkIn || '',
        checkOut: record?.checkOut || '',
        status: record?.status || 'present',
        notes: record?.notes || '',
        department: record?.department || ''
      });
    }
  }, [record]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Validate required fields
    if (!formData?.employeeName || !formData?.date) {
      return;
    }

    onSave({
      ...record,
      ...formData
    });
    onClose();
  };

  const calculateTotalHours = () => {
    if (!formData?.checkIn || !formData?.checkOut) return '--';
    
    const [inHour, inMin] = formData?.checkIn?.split(':')?.map(Number);
    const [outHour, outMin] = formData?.checkOut?.split(':')?.map(Number);
    
    const inMinutes = inHour * 60 + inMin;
    const outMinutes = outHour * 60 + outMin;
    const totalMinutes = outMinutes - inMinutes;
    
    if (totalMinutes < 0) return 'Invalid';
    
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    return `${hours}h ${minutes}m`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1000 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex-1 min-w-0 mr-4">
            <h2 className="text-lg md:text-xl font-semibold text-foreground truncate">Edit Attendance Record</h2>
            <p className="text-xs md:text-sm text-muted-foreground mt-1 truncate">
              Modify attendance details for {formData?.employeeName}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="flex-shrink-0"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Form */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          {/* Employee Information */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Employee Name"
              value={formData?.employeeName}
              onChange={(e) => handleInputChange('employeeName', e?.target?.value)}
              required
              disabled
            />
            <Input
              label="Employee ID"
              value={formData?.employeeId}
              onChange={(e) => handleInputChange('employeeId', e?.target?.value)}
              disabled
            />
          </div>

          {/* Date and Department */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
              value={formData?.date}
              onChange={(e) => handleInputChange('date', e?.target?.value)}
              required
            />
            <Input
              label="Department"
              value={formData?.department}
              onChange={(e) => handleInputChange('department', e?.target?.value)}
              disabled
            />
          </div>

          {/* Time Information */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Input
              label="Check In Time"
              type="time"
              value={formData?.checkIn}
              onChange={(e) => handleInputChange('checkIn', e?.target?.value)}
            />
            <Input
              label="Check Out Time"
              type="time"
              value={formData?.checkOut}
              onChange={(e) => handleInputChange('checkOut', e?.target?.value)}
            />
            <div className="flex flex-col">
              <label className="text-sm font-medium text-foreground mb-2">Total Hours</label>
              <div className="h-10 px-3 py-2 bg-muted rounded-md border border-border flex items-center">
                <span className="text-sm text-foreground">{calculateTotalHours()}</span>
              </div>
            </div>
          </div>

          {/* Status */}
          <Select
            label="Attendance Status"
            options={statusOptions}
            value={formData?.status}
            onChange={(value) => handleInputChange('status', value)}
            name="status"
            onValueChange={(value) => handleInputChange('status', value)}
            error=""
            helperText=""
            onOpenChange={() => {}}
            description=""
            selectId="attendance-status"
            ref={null}
          />

          {/* Notes */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">Notes</label>
            <textarea
              className="w-full px-3 py-2 border border-border rounded-md bg-input text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
              rows={3}
              placeholder="Add any notes or comments about this attendance record..."
              value={formData?.notes}
              onChange={(e) => handleInputChange('notes', e?.target?.value)}
            />
          </div>

          {/* Time Validation Warning */}
          {formData?.checkIn && formData?.checkOut && calculateTotalHours() === 'Invalid' && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
                <div>
                  <p className="text-sm font-medium text-error">Invalid Time Range</p>
                  <p className="text-sm text-foreground mt-1">
                    Check out time cannot be earlier than check in time.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Policy Information */}
          <div className="bg-muted/30 rounded-lg p-4">
            <h4 className="text-sm font-medium text-foreground mb-2">Attendance Policy</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Standard working hours: 9:00 AM - 6:00 PM</li>
              <li>• Late arrival: Check-in after 9:15 AM</li>
              <li>• Early departure: Check-out before 5:45 PM</li>
              <li>• Overtime: Working beyond 6:00 PM</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!formData?.employeeName || !formData?.date}
            className="w-full sm:w-auto"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditAttendanceModal;